<template>
  <div
    class="cms-progress"
    :class="[
      `cms-progress--${type}`,
      statusClass,
      {
        'cms-progress--without-text': !showText,
        'cms-progress--text-inside': textInside || textPosition === 'inside',
        [`cms-progress--${size}`]: size
      }
    ]"
    role="progressbar"
    :aria-valuenow="percentage"
    aria-valuemin="0"
    aria-valuemax="100"
  >
    <!-- 线形进度条 -->
    <div v-if="type === 'line'" class="cms-progress-bar">
      <div
        class="cms-progress-bar__outer"
        :style="{ height: `${strokeWidth}px` }"
      >
        <div
          class="cms-progress-bar__inner"
          :class="[
            { 'cms-progress-bar__inner--striped': striped },
            { 'cms-progress-bar__inner--striped-flow': stripedFlow }
          ]"
          :style="barStyle"
        >
          <div
            v-if="
              ((showText || $slots.default) && textInside) ||
              textPosition === 'inside'
            "
            class="cms-progress-bar__inner-text"
          >
            <slot>{{ content }}</slot>
          </div>
        </div>
      </div>
    </div>

    <!-- 环形进度条 -->
    <div
      v-else
      class="cms-progress-circle"
      :style="{ height: `${width}px`, width: `${width}px` }"
    >
      <svg viewBox="0 0 100 100">
        <!-- 背景圆环 -->
        <path
          class="cms-progress-circle__track"
          :d="trackPath"
          stroke="#e5e9f2"
          :stroke-width="relativeStrokeWidth"
          fill="none"
          :style="trailPathStyle"
        />
        <!-- 进度圆环 -->
        <path
          class="cms-progress-circle__path"
          :d="trackPath"
          :stroke="stroke"
          fill="none"
          :stroke-linecap="strokeLinecap"
          :stroke-width="relativeStrokeWidth"
          :style="circlePathStyle"
        />
      </svg>
    </div>

    <!-- 外部文本 -->
    <div
      v-if="
        (showText || $slots.default) &&
        !textInside &&
        textPosition !== 'inside' &&
        type === 'line'
      "
      class="cms-progress-text"
    >
      <slot>
        <!-- 状态图标 -->
        <span v-if="status" class="cms-progress-text__icon">
          <i v-if="status === 'success'" class="cms-icon-check" />
          <i v-else-if="status === 'warning'" class="cms-icon-warning" />
          <i v-else-if="status === 'exception'" class="cms-icon-close">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024">
              <path
                fill="currentColor"
                d="m466.752 512-90.496-90.496a32 32 0 0 1 45.248-45.248L512 466.752l90.496-90.496a32 32 0 1 1 45.248 45.248L557.248 512l90.496 90.496a32 32 0 1 1-45.248 45.248L512 557.248l-90.496 90.496a32 32 0 0 1-45.248-45.248z"
              ></path>
              <path
                fill="currentColor"
                d="M512 896a384 384 0 1 0 0-768 384 384 0 0 0 0 768m0 64a448 448 0 1 1 0-896 448 448 0 0 1 0 896"
              ></path>
            </svg>
          </i>
        </span>
        <span v-else>{{ content }}</span>
      </slot>
    </div>

    <!-- 环形进度条中间文本 -->
    <div
      v-if="
        (showText || $slots.default) &&
        (type === 'circle' || type === 'dashboard')
      "
      class="cms-progress-circle__text"
    >
      <slot>
        <!-- 状态图标 -->
        <span v-if="status" class="cms-progress-text__icon">
          <i v-if="status === 'success'" class="cms-icon-check" />
          <i v-else-if="status === 'warning'" class="cms-icon-warning" />
          <i v-else-if="status === 'exception'" class="cms-icon-close" />
        </span>
        <span v-else>{{ content }}</span>
      </slot>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { progressProps, progressEmits } from './progress'

defineOptions({
  name: 'CmsProgress'
})

const props = defineProps(progressProps)
const emit = defineEmits(progressEmits)

// 计算状态类名
const statusClass = computed(() => {
  if (props.status) return `is-${props.status}`
  return ''
})

// 计算进度条文本内容
const content = computed(() => {
  if (props.format) {
    return props.format(props.percentage)
  }
  return `${props.percentage}%`
})

// 计算线性进度条样式
const barStyle = computed(() => {
  const style: Record<string, string> = {}
  style.width = `${props.percentage}%`
  style.animationDuration = props.animated ? '2s' : '0s'

  if (props.color) {
    if (typeof props.color === 'function') {
      style.backgroundColor = props.color(props.percentage)
    } else if (Array.isArray(props.color)) {
      // 如果是数组，根据百分比计算渐变色
      const colorArray = props.color
      const colorLength = colorArray.length
      const segments = 100 / (colorLength - 1)

      if (colorLength >= 2) {
        let gradients = 'linear-gradient(to right'
        colorArray.forEach((color, index) => {
          gradients += `, ${color} ${index * segments}%`
        })
        gradients += ')'
        style.backgroundImage = gradients
        style.backgroundColor = 'transparent'
      } else if (colorLength === 1) {
        style.backgroundColor = colorArray[0]
      }
    } else {
      style.backgroundColor = props.color
    }
  }

  return style
})

// 环形进度条相关计算
const relativeStrokeWidth = computed(() => {
  return (props.strokeWidth / props.width) * 100
})

// 计算圆环路径
const radius = computed(() => {
  if (props.type === 'circle' || props.type === 'dashboard') {
    return 50 - parseFloat(relativeStrokeWidth.value + '') / 2
  }
  return 0
})

// 计算环形进度条的周长
const perimeter = computed(() => {
  return 2 * Math.PI * radius.value
})

// 计算仪表盘的缺口角度（转换为弧度）
const gapDegree = computed(() => {
  return props.type === 'dashboard' ? props.gapDegree : 0
})

// 计算圆环路径的起始角度（转换为弧度）
const startAngle = computed(() => {
  return -Math.PI / 2 + (gapDegree.value / 2) * (Math.PI / 180)
})

// 计算圆环路径的结束角度（转换为弧度）
const endAngle = computed(() => {
  return Math.PI * 2 * (props.percentage / 100) + startAngle.value
})

// 计算圆环路径
const trackPath = computed(() => {
  const radius = 50 - parseFloat(relativeStrokeWidth.value + '') / 2

  // 仪表盘类型，带有缺口
  if (props.type === 'dashboard') {
    const gapRadian = (props.gapDegree / 180) * Math.PI
    const startPoint = polarToCartesian(50, 50, radius, startAngle.value)
    const endPoint = polarToCartesian(
      50,
      50,
      radius,
      Math.PI * 2 - gapRadian / 2
    )

    return `M ${startPoint.x} ${startPoint.y} A ${radius} ${radius} 0 1 1 ${endPoint.x} ${endPoint.y}`
  }

  // 普通圆环
  return `M 50,50 m 0,-${radius} a ${radius},${radius} 0 1 1 0,${
    2 * radius
  } a ${radius},${radius} 0 1 1 0,-${2 * radius}`
})

// 计算圆环路径样式
const circlePathStyle = computed(() => {
  const style: Record<string, string> = {}

  // 计算进度条长度
  if (props.type === 'dashboard') {
    const gapRadian = (props.gapDegree / 180) * Math.PI
    const dashPerimeter = perimeter.value - gapRadian * radius.value
    const dashOffset = dashPerimeter * (1 - props.percentage / 100)

    style.strokeDasharray = `${dashPerimeter}px, ${perimeter.value}px`
    style.strokeDashoffset = `${dashOffset}px`
  } else {
    style.strokeDasharray = `${perimeter.value * (props.percentage / 100)}px, ${
      perimeter.value
    }px`
  }

  // 设置动画
  style.transition = props.animated
    ? 'stroke-dasharray 0.6s ease 0s, stroke 0.6s ease 0s'
    : ''

  return style
})

// 轨道样式
const trailPathStyle = computed(() => {
  const style: Record<string, string> = {}

  if (props.type === 'dashboard') {
    const gapRadian = (props.gapDegree / 180) * Math.PI
    const dashPerimeter = perimeter.value - gapRadian * radius.value

    style.strokeDasharray = `${dashPerimeter}px, ${perimeter.value}px`
  }

  return style
})

// 计算线段端点样式
const strokeLinecap = computed(() => {
  return props.type === 'dashboard' ? 'butt' : 'round'
})

// 计算环形进度条颜色
const stroke = computed(() => {
  let color = ''

  if (props.color) {
    if (typeof props.color === 'function') {
      color = props.color(props.percentage)
    } else if (Array.isArray(props.color)) {
      // 如果是数组，根据百分比计算颜色
      const colorArray = props.color
      const colorLength = colorArray.length

      if (colorLength >= 2) {
        const index = Math.floor(props.percentage / (100 / (colorLength - 1)))
        const ratio =
          (props.percentage % (100 / (colorLength - 1))) /
          (100 / (colorLength - 1))

        if (index === colorLength - 1) {
          color = colorArray[index]
        } else {
          // 计算两个颜色之间的过渡
          color = calculateColorTransition(
            colorArray[index],
            colorArray[index + 1],
            ratio
          )
        }
      } else if (colorLength === 1) {
        color = colorArray[0]
      }
    } else {
      color = props.color
    }
  } else {
    // 根据状态设置默认颜色
    if (props.status === 'success') {
      color = '#67c23a'
    } else if (props.status === 'exception') {
      color = '#f56c6c'
    } else if (props.status === 'warning') {
      color = '#e6a23c'
    } else {
      color = '#409eff'
    }
  }

  return color
})

// 极坐标转笛卡尔坐标
function polarToCartesian(
  cx: number,
  cy: number,
  radius: number,
  angle: number
) {
  return {
    x: cx + radius * Math.cos(angle),
    y: cy + radius * Math.sin(angle)
  }
}

// 计算两个颜色之间的过渡
function calculateColorTransition(
  color1: string,
  color2: string,
  ratio: number
) {
  // 简单实现，仅支持十六进制颜色
  if (color1.startsWith('#') && color2.startsWith('#')) {
    const r1 = parseInt(color1.slice(1, 3), 16)
    const g1 = parseInt(color1.slice(3, 5), 16)
    const b1 = parseInt(color1.slice(5, 7), 16)

    const r2 = parseInt(color2.slice(1, 3), 16)
    const g2 = parseInt(color2.slice(3, 5), 16)
    const b2 = parseInt(color2.slice(5, 7), 16)

    const r = Math.round(r1 + (r2 - r1) * ratio)
    const g = Math.round(g1 + (g2 - g1) * ratio)
    const b = Math.round(b1 + (b2 - b1) * ratio)

    return `#${r.toString(16).padStart(2, '0')}${g
      .toString(16)
      .padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
  }

  return color1
}
</script>

<style>
.cms-progress {
  --cms-progress-font-size: 14px;
  --cms-progress-text-color: #606266;
  --cms-progress-success-color: #67c23a;
  --cms-progress-warning-color: #e6a23c;
  --cms-progress-exception-color: #f56c6c;
  --cms-progress-info-color: #909399;
  --cms-progress-primary-color: #409eff;
  --cms-progress-bg-color: #ebeef5;

  position: relative;
  line-height: 1;
}

/* 线形进度条样式 */
.cms-progress--line {
  margin-bottom: 15px;
  width: 100%;
}

.cms-progress-bar {
  width: 100%;
  padding-right: 50px;
  box-sizing: border-box;
}

.cms-progress-bar__outer {
  height: 6px;
  border-radius: 100px;
  background-color: var(--cms-progress-bg-color);
  overflow: hidden;
  position: relative;
  vertical-align: middle;
}

.cms-progress-bar__inner {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background-color: var(--cms-progress-primary-color);
  text-align: right;
  border-radius: 100px;
  line-height: 1;
  white-space: nowrap;
  transition: width 0.6s ease;
}

/* 条纹进度条 */
.cms-progress-bar__inner--striped {
  background-image: linear-gradient(
    45deg,
    rgba(255, 255, 255, 0.15) 25%,
    transparent 25%,
    transparent 50%,
    rgba(255, 255, 255, 0.15) 50%,
    rgba(255, 255, 255, 0.15) 75%,
    transparent 75%,
    transparent
  );
  background-size: 40px 40px;
}

/* 流动条纹 */
.cms-progress-bar__inner--striped-flow {
  animation: cms-progress-bar-stripes 2s linear infinite;
}

@keyframes cms-progress-bar-stripes {
  from {
    background-position: 40px 0;
  }
  to {
    background-position: 0 0;
  }
}

.cms-progress-bar__inner-text {
  display: inline-block;
  vertical-align: middle;
  color: #fff;
  font-size: 12px;
  margin: 0 5px;
  white-space: nowrap;
}

/* 文本样式 */
.cms-progress-text {
  font-size: var(--cms-progress-font-size);
  color: var(--cms-progress-text-color);
  margin-left: 5px;
  min-width: 50px;
  line-height: 1;
  position: absolute;
  top: 0;
  right: 0;
}

.cms-progress-text__icon {
  font-size: 16px;
}

/* 内嵌文本的进度条 */
.cms-progress--text-inside .cms-progress-bar {
  padding-right: 0;
  margin-right: 0;
}

/* 环形进度条 */
.cms-progress--circle,
.cms-progress--dashboard {
  display: inline-block;
}

.cms-progress-circle {
  position: relative;
}

.cms-progress-circle__text {
  position: absolute;
  top: 50%;
  left: 0;
  width: 100%;
  text-align: center;
  transform: translateY(-50%);
  font-size: var(--cms-progress-font-size);
  color: var(--cms-progress-text-color);
}

/* 状态样式 */
.cms-progress.is-success .cms-progress-bar__inner {
  background-color: var(--cms-progress-success-color);
}

.cms-progress.is-success .cms-progress-text {
  color: var(--cms-progress-success-color);
}

.cms-progress.is-warning .cms-progress-bar__inner {
  background-color: var(--cms-progress-warning-color);
}

.cms-progress.is-warning .cms-progress-text {
  color: var(--cms-progress-warning-color);
}

.cms-progress.is-exception .cms-progress-bar__inner {
  background-color: var(--cms-progress-exception-color);
}

.cms-progress.is-exception .cms-progress-text {
  color: var(--cms-progress-exception-color);
}

/* 不同尺寸 */
.cms-progress--small .cms-progress-bar__outer {
  height: 4px;
}

.cms-progress--small .cms-progress-text {
  font-size: 12px;
}

.cms-progress--large .cms-progress-bar__outer {
  height: 8px;
}

.cms-progress--large .cms-progress-text {
  font-size: 16px;
}
</style>
